import { createPortal } from 'react-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Building2, MapPin, Plus } from 'lucide-react'
import { SPACE_TYPES } from '../constants'

const CreateSpaceModal = ({
  show,
  onClose,
  spaceForm,
  onFormChange,
  onSubmit,
  creatingSpace,
  spaceFormError,
  facilitators,
}) => {
  return createPortal(
    <AnimatePresence>
      {show && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {/* Backdrop */}
          <motion.div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />

          {/* Modal */}
          <motion.div
            className="relative bg-slate-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden border border-white/20"
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <Building2 className="w-6 h-6 text-purple-400" />
                Create New Space
              </h2>
              <button
                onClick={onClose}
                className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors"
              >
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={onSubmit} className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
              <div className="space-y-6">
                {/* Facilitator Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Facilitator Owner <span className="text-red-400">*</span>
                  </label>
                  <select
                    name="facilitatorProfileId"
                    value={spaceForm.facilitatorProfileId}
                    onChange={onFormChange}
                    className="w-full px-4 py-3 bg-slate-700 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent [&>option]:bg-slate-700 [&>option]:text-white"
                    required
                  >
                    <option value="">Select a facilitator...</option>
                    {facilitators?.map((fac) => (
                      <option key={fac.profileId} value={fac.profileId}>
                        {fac.name} ({fac.email})
                      </option>
                    ))}
                  </select>
                </div>

                {/* Space Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Space Name <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={spaceForm.name}
                    onChange={onFormChange}
                    placeholder="e.g., Sunrise Yoga Studio"
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    required
                  />
                </div>

                {/* Space Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Space Type <span className="text-red-400">*</span>
                  </label>
                  <select
                    name="spaceType"
                    value={spaceForm.spaceType}
                    onChange={onFormChange}
                    className="w-full px-4 py-3 bg-slate-700 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent [&>option]:bg-slate-700 [&>option]:text-white"
                    required
                  >
                    <option value="">Select space type...</option>
                    {SPACE_TYPES.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={spaceForm.description}
                    onChange={onFormChange}
                    placeholder="Brief description of the space..."
                    rows={3}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                  />
                </div>

                {/* Location Section */}
                <div className="border-t border-white/10 pt-6">
                  <h3 className="text-sm font-semibold text-gray-300 uppercase tracking-wider mb-4 flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    Location
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-400 mb-1">Address</label>
                      <input
                        type="text"
                        name="addressLine1"
                        value={spaceForm.addressLine1}
                        onChange={onFormChange}
                        placeholder="Street address"
                        className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-1">City</label>
                      <input
                        type="text"
                        name="city"
                        value={spaceForm.city}
                        onChange={onFormChange}
                        placeholder="City"
                        className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-1">State</label>
                      <input
                        type="text"
                        name="state"
                        value={spaceForm.state}
                        onChange={onFormChange}
                        placeholder="State"
                        className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-1">Pincode</label>
                      <input
                        type="text"
                        name="pincode"
                        value={spaceForm.pincode}
                        onChange={onFormChange}
                        placeholder="Pincode"
                        className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-1">Capacity</label>
                      <input
                        type="number"
                        name="capacity"
                        value={spaceForm.capacity}
                        onChange={onFormChange}
                        placeholder="Max capacity"
                        min="1"
                        className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-400 mb-1">Google Maps URL</label>
                      <input
                        type="url"
                        name="googleMapsUrl"
                        value={spaceForm.googleMapsUrl}
                        onChange={onFormChange}
                        placeholder="https://maps.google.com/..."
                        className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>

                {/* Contact Section */}
                <div className="border-t border-white/10 pt-6">
                  <h3 className="text-sm font-semibold text-gray-300 uppercase tracking-wider mb-4">
                    Contact Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-1">Contact Name</label>
                      <input
                        type="text"
                        name="contactName"
                        value={spaceForm.contactName}
                        onChange={onFormChange}
                        placeholder="Contact person"
                        className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-1">Phone</label>
                      <input
                        type="tel"
                        name="contactPhone"
                        value={spaceForm.contactPhone}
                        onChange={onFormChange}
                        placeholder="+91 98765 43210"
                        className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-1">Email</label>
                      <input
                        type="email"
                        name="contactEmail"
                        value={spaceForm.contactEmail}
                        onChange={onFormChange}
                        placeholder="contact@example.com"
                        className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>

                {/* Error Message */}
                {spaceFormError && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-4 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400 text-sm"
                  >
                    {spaceFormError}
                  </motion.div>
                )}
              </div>

              {/* Footer */}
              <div className="flex items-center justify-end gap-3 mt-6 pt-6 border-t border-white/10">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-6 py-2.5 bg-white/10 text-white rounded-lg font-medium hover:bg-white/20 transition-colors"
                  disabled={creatingSpace}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creatingSpace}
                  className="px-6 py-2.5 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {creatingSpace ? (
                    <>
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                        className="w-4 h-4 border-2 border-white border-t-transparent rounded-full"
                      />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4" />
                      Create Space
                    </>
                  )}
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  )
}

export default CreateSpaceModal
